import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useReadinessStore } from '../store/readinessStore';

const THRESHOLDS = [
  { pct: 75, label: 'Demo Day ready', color: '#22c55e' },
  { pct: 50, label: 'Launch ready', color: '#f59e0b' },
  { pct: 25, label: 'Getting started', color: '#7c6ff7' },
];

function getMotivation(score: number): string {
  const remaining = 75 - score;
  if (score >= 100) return "You're fully battle-tested — time to raise or scale!";
  if (score >= 75) return "Most accelerators expect 75% before Demo Day — you're there!";
  if (remaining > 0) return `${remaining} more points to hit 75% — the threshold most accelerators expect before Demo Day.`;
  return 'Keep going — every completed item sharpens your edge.';
}

export function ReadinessScorecard() {
  const { score, checklist, isOpen, closePanel } = useReadinessStore();

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closePanel(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closePanel]);

  if (!isOpen) return null;

  const scoreColor = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#7c6ff7';
  const threshold = THRESHOLDS.find(t => score >= t.pct);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closePanel}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          zIndex: 1000, backdropFilter: 'blur(2px)',
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 420,
        background: '#0d0f1a', borderLeft: '1px solid #2a2a3e',
        zIndex: 1001, overflowY: 'auto', padding: '24px 24px 40px',
        animation: 'wizard-slide-fwd 0.25s ease',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
              Startup Readiness Score
            </h2>
            {threshold && (
              <span style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 6,
                background: `${threshold.color}15`, color: threshold.color,
                border: `1px solid ${threshold.color}40`, fontWeight: 600,
              }}>{threshold.label}</span>
            )}
          </div>
          <button
            onClick={closePanel}
            style={{
              background: '#1e1e30', border: '1px solid #2a2a3e', borderRadius: 8,
              color: '#9ca3af', fontSize: 16, cursor: 'pointer',
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Score circle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24, padding: '16px 20px', background: '#13152a', borderRadius: 12, border: '1px solid #1e2140' }}>
          <svg width={80} height={80} viewBox="0 0 80 80">
            <circle cx={40} cy={40} r={32} fill="none" stroke="#1e1e30" strokeWidth={8} />
            <circle cx={40} cy={40} r={32} fill="none" stroke={scoreColor} strokeWidth={8}
              strokeDasharray={2 * Math.PI * 32}
              strokeDashoffset={2 * Math.PI * 32 - (score / 100) * 2 * Math.PI * 32}
              strokeLinecap="round" transform="rotate(-90 40 40)"
              style={{ transition: 'stroke-dashoffset 1s ease' }} />
            <text x={40} y={44} textAnchor="middle" fill={scoreColor} fontSize={20} fontWeight={700}>{score}</text>
          </svg>
          <div>
            <p style={{ fontSize: 28, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{score}<span style={{ fontSize: 16, color: '#6b7280' }}>/100</span></p>
            <div style={{ height: 6, width: 160, background: '#1e1e30', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${score}%`, borderRadius: 3,
                background: `linear-gradient(90deg, #7c6ff7, ${scoreColor})`,
                transition: 'width 0.8s ease',
              }} />
            </div>
          </div>
        </div>

        {/* Checklist */}
        {checklist.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
            {checklist.map(item => (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                background: '#13152a', borderRadius: 8,
                border: `1px solid ${item.done ? '#22c55e20' : '#1e2140'}`,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0, color: item.done ? '#22c55e' : '#2a2a3e' }}>
                  {item.done ? '✅' : '⬜'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, color: item.done ? '#d1d5db' : '#6b7280', marginBottom: 1 }}>{item.label}</p>
                  <p style={{ fontSize: 11, color: '#4b5563' }}>{item.points} pts</p>
                </div>
                {!item.done && item.link && (
                  <Link
                    to={item.link}
                    onClick={closePanel}
                    style={{
                      fontSize: 11, color: '#7c6ff7', background: 'rgba(124,111,247,0.1)',
                      border: '1px solid rgba(124,111,247,0.25)', borderRadius: 6,
                      padding: '3px 8px', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0,
                    }}
                  >→ Do it</Link>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#4b5563', fontSize: 13 }}>
            <p style={{ marginBottom: 12 }}>Visit your startup dashboard to compute your readiness score.</p>
            <Link
              to="/startups"
              onClick={closePanel}
              style={{
                display: 'inline-block', background: '#7c6ff7', color: '#fff',
                borderRadius: 10, padding: '8px 20px', fontSize: 13, fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Go to My Startups →
            </Link>
          </div>
        )}

        {/* Motivational footer */}
        <div style={{
          padding: '12px 16px', background: 'rgba(124,111,247,0.06)',
          border: '1px solid rgba(124,111,247,0.2)', borderRadius: 10,
          fontSize: 13, color: '#9ca3af', lineHeight: 1.5,
        }}>
          💡 {getMotivation(score)}
        </div>
      </div>
    </>
  );
}
